import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  Box,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import InfoIcon from "@material-ui/icons/Info";
import NetworkCheckIcon from "@material-ui/icons/NetworkCheck";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { Alert } from "@material-ui/lab";

const ICONS_BY_TYPE = {
  started: <PlayArrowIcon color="primary" />,
  step: <InfoIcon color="disabled" />,
  warning: <WarningIcon style={{ color: "#ed6c02" }} />,
  cloud_connection: <NetworkCheckIcon color="primary" />,
  forwarding: <NetworkCheckIcon color="primary" />,
  forwarded_port: <NetworkCheckIcon color="primary" />,
  port_test: <NetworkCheckIcon color="primary" />,
  port_summary: <InfoIcon color="primary" />,
  completed: <CheckCircleIcon style={{ color: "#2e7d32" }} />,
  error: <ErrorIcon color="error" />,
};

const getIcon = (type) => ICONS_BY_TYPE[type] || <HelpOutlineIcon />;

const parseSseFrame = (frame) => {
  const lines = frame.split("\n").filter((line) => !line.startsWith(":"));
  let event = "message";
  const dataLines = [];

  lines.forEach((line) => {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  });

  if (dataLines.length === 0) {
    return null;
  }

  const rawData = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(rawData) };
  } catch (e) {
    return { event, data: { type: "unknown", message: rawData } };
  }
};

const VerifyPortForwardResult = ({
  open,
  onClose,
  deviceSerial,
  fix,
  scanLan,
}) => {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("connecting");
  const [errorMessage, setErrorMessage] = useState(null);
  const listEndRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setEvents([]);
    setStatus("connecting");
    setErrorMessage(null);

    const controller = new AbortController();
    let reader = null;

    const run = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/device/port_forward/verify/${deviceSerial}?fix=${fix}&scan_lan=${scanLan}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              Accept: "text/event-stream",
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          let detail = "Failed to start verification";
          try {
            const body = await response.json();
            detail = body?.detail || detail;
          } catch (e) {
            // ignore non-JSON error bodies
          }
          setErrorMessage(detail);
          setStatus("error");
          return;
        }

        setStatus("streaming");

        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop();

          frames.forEach((frame) => {
            const parsed = parseSseFrame(frame);
            if (!parsed) {
              return;
            }
            setEvents((prev) => [...prev, parsed]);
            if (parsed.event === "completed" || parsed.data?.type === "completed") {
              setStatus("completed");
            }
          });
        }

        setStatus((prev) => (prev === "error" ? prev : "completed"));
      } catch (e) {
        if (e.name === "AbortError") {
          return;
        }
        setErrorMessage(e.message || "Connection failed");
        setStatus("error");
      }
    };

    run();

    return () => {
      controller.abort();
      if (reader) {
        reader.cancel().catch(() => {});
      }
    };
  }, [open, deviceSerial, fix, scanLan]);

  useEffect(() => {
    if (listEndRef.current) {
      listEndRef.current.scrollIntoView({ block: "end" });
    }
  }, [events.length]);

  const renderStatusChip = () => {
    if (status === "connecting") {
      return <Chip size="small" icon={<CircularProgress size={16} />} label="Connecting…" />;
    }
    if (status === "streaming") {
      return <Chip size="small" color="primary" label="Live" />;
    }
    if (status === "completed") {
      return <Chip size="small" style={{ backgroundColor: "#2e7d32", color: "#fff" }} label="Completed" />;
    }
    if (status === "error") {
      return <Chip size="small" color="secondary" label="Error" />;
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Verifying Port Forwarding — {deviceSerial}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" style={{ marginBottom: "12px" }}>
          {renderStatusChip()}
        </Box>
        {status === "error" && (
          <Alert severity="error" style={{ marginBottom: "12px" }}>
            {errorMessage}
          </Alert>
        )}
        <Box
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
          }}
        >
          <List dense>
            {events.map((item, index) => (
              <ListItem key={index}>
                <ListItemIcon style={{ minWidth: "36px" }}>
                  {getIcon(item.data?.type || item.event)}
                </ListItemIcon>
                <ListItemText primary={item.data?.message || JSON.stringify(item.data)} />
              </ListItem>
            ))}
            <div ref={listEndRef} />
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerifyPortForwardResult;
